<template>
  <div class="orders-container app-workbench app-page">
    <!-- 搜索和订单类型筛选 -->
    <div class="order-toolbar">
      <div class="search-area">
        <el-input
          v-model="queryDTO.keyword"
          placeholder="搜索订单编号、收件人等"
          clearable
          @clear="handleSearch"
          @keyup.enter="handleSearch"
          class="order-search-input"
        >
          <template #prefix>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </template>
          <template #append>
            <el-button @click="handleSearch">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </el-button>
          </template>
        </el-input>
      </div>
      <div class="type-filter">
        <el-radio-group v-model="orderType" @change="handleTypeChange" size="small">
          <el-radio-button label="my">我的订单</el-radio-button>
          <el-radio-button label="gift">礼物订单</el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <!-- 礼物订单二级筛选 -->
    <div class="gift-sub-filter" v-if="orderType === 'gift'">
      <el-radio-group v-model="giftSubType" @change="handleGiftSubTypeChange" size="small">
        <el-radio-button label="received">我收到的</el-radio-button>
        <el-radio-button label="sent">我送出的</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 订单状态筛选 -->
    <div class="status-filter">
      <el-radio-group v-model="queryDTO.status" @change="handleStatusChange" size="small">
        <el-radio-button label="">全部</el-radio-button>
        <el-radio-button label="0">待付款</el-radio-button>
        <el-radio-button label="1">已付款</el-radio-button>
        <el-radio-button label="2">已取消</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 订单列表 -->
    <el-card class="order-table-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="orderList"
        style="width: 100%"
        empty-text="暂无订单数据"
        class="order-table"
      >
        <el-table-column prop="orderSn" label="订单编号" width="180">
          <template #default="scope">
            <span class="order-sn">{{ scope.row.orderSn }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="总金额"
          width="120"
          align="left"
        >
          <template #default="scope">
            <span class="order-amount">¥{{ formatPrice(scope.row.totalAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="status"
          label="状态"
          width="110"
        >
          <template #default="scope">
            <el-tag :type="getStatusTagType(scope.row.status)" class="status-tag-badge" effect="dark">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="类型"
          width="100"
        >
          <template #default="scope">
            <el-tag type="info" class="type-tag" effect="plain">
              {{ scope.row.isGift ? '礼物订单' : '普通订单' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="相关用户"
          width="180"
        >
          <template #default="scope">
            <span class="related-user">
              {{ scope.row.isGift
                ? (scope.row.userId === queryDTO.userId ? '送给: ' + scope.row.receiver : '来自: ' + scope.row.username)
                : '-'
              }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180" :formatter="formatDate">
          <template #default="scope">
            <span class="order-time">{{ formatDate(scope.row, scope.column, scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="150"
          fixed="right"
        >
          <template #default="scope">
            <div class="order-actions">
              <el-button
                type="text"
                class="action-link"
                @click="handleViewDetail(scope.row)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                详情
              </el-button>
              <el-button
                type="text"
                :disabled="scope.row.status !== 0"
                :class="{ 'pay-link': scope.row.status === 0 }"
                @click="handlePay(scope.row)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                付款
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 分页 -->
    <div class="pagination-container" v-if="total > 0">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="page"
        :page-sizes="[5, 10, 20]"
        :page-size="size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      ></el-pagination>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, toRefs } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage } from 'element-plus'

// 路由
const router = useRouter()

// 基础API路径
const BASE_URL = '/product-service/api/order'

// 订单相关API
const orderApi = {
  // 动态查询订单列表
  getOrderList: (params) => axios.get(`${BASE_URL}/list`, { params })
}

// 状态管理
const state = reactive({
  orderList: [],
  loading: false,
  total: 0,
  page: 1,
  size: 10,
  orderType: 'all', // 订单类型筛选：all-全部，my-我的订单，gift-礼物订单
  giftSubType: 'received', // 礼物订单子类型：received-我收到的（默认），sent-我送出的
  queryDTO: {
    keyword: '',
    status: '', // 空表示全部状态
    userId: Number(localStorage.getItem('userId')), // 从本地存储获取用户ID
    isGift: null, // true-礼物订单 false-普通订单 null-全部
    receiverUserId: null
  }
})

const {
  orderList,
  loading,
  total,
  page,
  size,
  orderType,
  giftSubType,
  queryDTO
} = toRefs(state)

// 初始化加载订单列表
onMounted(() => {
  if (!queryDTO.value.userId) {
    ElMessage.warning('请先登录')
    router.push('/login')
    return
  }
  fetchOrderList()
})

// 获取订单列表
const fetchOrderList = async () => {
  try {
    loading.value = true

    // 构建查询参数
    const params = {
      ...queryDTO.value,
      page: page.value - 1, // 后端接口页码从0开始
      size: size.value,
      sortField: 'createTime',
      direction: 'desc'
    }

    // 移除null/空值参数
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined || params[key] === '') {
        delete params[key]
      }
    })

    const res = await orderApi.getOrderList(params)

    if (res.data.code == 200) {
      orderList.value = res.data.data.content || []
      total.value = res.data.data.totalElements || 0
    } else {
      ElMessage.error(res.data.message || '获取订单列表失败')
    }
  } catch (error) {
    ElMessage.error('获取订单列表失败：' + (error.response?.data?.msg || error.message))
  } finally {
    loading.value = false
  }
}

// 格式化价格
const formatPrice = (price) => {
  if (!price) return '0.00'
  // 处理可能的数字或字符串类型价格
  return Number(price).toFixed(2)
}

// 格式化日期
const formatDate = (row, column, cellValue) => {
  if (!cellValue) return ''
  const date = new Date(cellValue)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 获取状态文本
const getStatusText = (status) => {
  const statusMap = {
    0: '待付款',
    1: '已付款',
    2: '已取消'
  }
  return statusMap[status] || '未知状态'
}

// 获取状态标签类型
const getStatusTagType = (status) => {
  const typeMap = {
    0: 'warning',
    1: 'success',
    2: 'danger'
  }
  return typeMap[status] || 'default'
}

// 订单类型变更处理
const handleTypeChange = () => {
  switch(orderType.value) {
    case 'all':
      queryDTO.value.isGift = null
      queryDTO.value.userId = Number(localStorage.getItem('userId'))
      queryDTO.value.receiverUserId = null
      break
    case 'my':
      queryDTO.value.isGift = false
      queryDTO.value.userId = Number(localStorage.getItem('userId'))
      queryDTO.value.receiverUserId = null
      break
    case 'gift': {  // 添加大括号创建块级作用域
      queryDTO.value.isGift = true
      // 礼物订单默认选中"我收到的"
      state.giftSubType = 'received'
      const userId = Number(localStorage.getItem('userId'))  // 现在变量声明在块级作用域内
      queryDTO.value.receiverUserId = userId
      queryDTO.value.userId = null
      break
    }  // 关闭块级作用域
  }
  page.value = 1 // 重置到第一页
  fetchOrderList()
}

// 礼物订单子类型变更处理
const handleGiftSubTypeChange = (subType) => {
  state.giftSubType = subType
  const userId = Number(localStorage.getItem('userId'))

  if (subType === 'received') {
    // 我收到的：传receiverUserId，不传userId
    queryDTO.value.receiverUserId = userId
    queryDTO.value.userId = null
  } else if (subType === 'sent') {
    // 我送出的：传userId，不传receiverUserId
    queryDTO.value.userId = userId
    queryDTO.value.receiverUserId = null
  }

  page.value = 1 // 重置到第一页
  fetchOrderList()
}

// 状态变更处理
const handleStatusChange = () => {
  page.value = 1 // 重置到第一页
  fetchOrderList()
}

// 搜索处理
const handleSearch = () => {
  page.value = 1 // 重置到第一页
  fetchOrderList()
}

// 分页大小变更
const handleSizeChange = (val) => {
  size.value = val
  page.value = 1 // 重置到第一页
  fetchOrderList()
}

// 当前页变更
const handleCurrentChange = (val) => {
  page.value = val
  fetchOrderList()
}

// 查看订单详情
const handleViewDetail = (row) => {
  router.push(`/order/detail/${row.orderSn}`)
}

// 去付款
const handlePay = (row) => {
  router.push(`/order/pay/${row.orderSn}`)
}

</script>

<style scoped>
.orders-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Toolbar */
.order-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search-area {
  flex: 1;
  min-width: 240px;
}

.order-search-input :deep(.el-input__wrapper) {
  border-radius: var(--app-radius-lg);
  box-shadow: 0 0 0 1px var(--app-border-light) inset;
}

.order-search-input :deep(.el-input-group__append) {
  border-radius: 0 var(--app-radius-lg) var(--app-radius-lg) 0;
}

/* Filters */
.gift-sub-filter {
  margin-bottom: 12px;
  padding: 12px 16px;
  background: var(--app-color-primary-muted);
  border-radius: var(--app-radius-lg);
  border: 1px solid rgba(255, 126, 126, 0.1);
}

.status-filter {
  margin-bottom: 20px;
  padding: 14px 16px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-lg);
  border: 1px solid var(--app-border-subtle);
}

:deep(.el-radio-group) {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

:deep(.el-radio-button__inner) {
  border: 1px solid var(--app-border-light) !important;
  border-radius: var(--app-radius-md) !important;
  padding: 8px 18px !important;
  font-weight: 500;
  transition: all 0.2s ease;
}

:deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
  background: var(--app-color-primary) !important;
  border-color: var(--app-color-primary) !important;
  color: #fff !important;
  box-shadow: none !important;
}

:deep(.el-radio-button:not(:first-child) .el-radio-button__inner) {
  border-left: 1px solid var(--app-border-light) !important;
}

/* Order table */
.order-table-card {
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  overflow: hidden;
}

.order-table {
  border-radius: var(--app-radius-lg);
  overflow: hidden;
}

.order-table :deep(.el-table__header th) {
  background: var(--app-bg-soft) !important;
  color: var(--app-text-secondary);
  font-weight: 600;
  font-size: 0.875rem;
}

.order-table :deep(.el-table__body td) {
  padding: 14px 0 !important;
}

.order-table :deep(.el-table__body tr:hover td) {
  background: var(--app-color-primary-muted) !important;
}

.order-sn {
  font-family: 'SF Mono', 'Courier New', monospace;
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
  letter-spacing: 0.5px;
}

.order-amount {
  font-weight: 700;
  color: var(--app-color-primary);
  font-size: 0.9375rem;
}

.status-tag-badge {
  border-radius: var(--app-radius-full) !important;
  font-weight: 500 !important;
  padding: 0 12px !important;
  border: none !important;
}

.type-tag {
  border-radius: var(--app-radius-full) !important;
  font-size: 0.75rem !important;
  padding: 0 10px !important;
}

.related-user {
  font-size: 0.875rem;
  color: var(--app-text-secondary);
}

.order-time {
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
}

.order-actions {
  display: flex;
  gap: 4px;
}

.action-link {
  font-size: 0.8125rem !important;
  color: var(--app-text-secondary) !important;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px !important;
  border-radius: var(--app-radius-sm) !important;
  transition: all 0.2s ease !important;
}

.action-link:hover {
  color: var(--app-color-primary) !important;
  background: var(--app-color-primary-soft) !important;
}

.pay-link {
  color: var(--app-color-primary) !important;
  font-weight: 500 !important;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px !important;
  border-radius: var(--app-radius-sm) !important;
  transition: all 0.2s ease !important;
}

.pay-link:hover:not(:disabled) {
  background: var(--app-color-primary-soft) !important;
  transform: scale(1.05);
}

.pay-link:disabled {
  color: var(--app-text-muted) !important;
  cursor: not-allowed;
}

/* Pagination */
.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

@media (max-width: 768px) {
  .order-toolbar {
    flex-direction: column;
  }

  .search-area {
    width: 100%;
  }
}
</style>
